import { Chip, CircularProgress } from "@mui/material";
import { useErrorMessage } from "@poseidon/ui/app/toasts/useErrorMessage";
import DataGrid from "@poseidon/ui/components/DataGrid";
import { DataGridContainer } from "@poseidon/ui/components/DataGridContainer";
import { useSailorContext } from "../SailorContext";
import { filterOperators, timestampColumn } from "../../DataGrid/ColumnHelpers";
import SailingIcon from "@mui/icons-material/Sailing";
import DangerousIcon from "@mui/icons-material/Dangerous";
import { camelToSentenceCase } from "@poseidon/utils/text";
import { getDate } from "@poseidon/firebase/timestamp";
import { formatCurrency } from "@poseidon/utils";
import { useSailorAgreements } from "../hooks/useSailorAgreements";
import PendingIcon from "@mui/icons-material/Pending";
import WarningIcon from "@mui/icons-material/Warning";
import React from "react";

const columns = [
  {
    field: "agreementNumber",
    headerName: "Agreement Number",
    width: 160,
    filterOperators,
    hideable: false,
  },
  {
    field: "status",
    headerName: "Status",
    width: 160,
    filterOperators,
    valueGetter: ({ value, row }) => {
      if (!row.remainingPayments && value === "active") {
        return "closing";
      }
      return value;
    },
    renderCell: ({ value }) => {
      if (value === "pending") {
        return <Chip icon={<PendingIcon />} label="Pending" />;
      }
      if (value === "active") {
        return <Chip color="primary" icon={<SailingIcon />} label="Active" />;
      }

      return (
        <Chip
          color="error"
          icon={<DangerousIcon />}
          label={camelToSentenceCase(value ?? "Invalid")}
        />
      );
    },
  },
  timestampColumn({
    field: "agreementCreation",
    headerName: "Created At",
  }),
  {
    field: "arrears",
    headerName: "Arrears",
    width: 320,
    filterOperators,
    renderCell: ({ row }) => {
      if (!row.duePayments) {
        return "";
      }
      return (
        <>
          <WarningIcon color="warning" sx={{ mr: 1 }} />
          {`${formatCurrency(row.arrears)} (${row.duePayments} payment${
            row.duePayments > 1 ? "s" : ""
          } - Due ${getDate(row.dateOfOldestDuePayment).toLocaleDateString(
            "en-GB"
          )})`}
        </>
      );
    },
  },
  {
    field: "capitalBalance",
    headerName: "Capital Balance",
    width: 210,
    filterOperators,
    renderCell: ({ row }) => {
      if (!row.schedule) {
        return "N/A";
      }
      if (row.capitalBalance === 0) {
        return "£0";
      }
      return `${formatCurrency(row.capitalBalance)} (${
        row.remainingPayments
      } payment${row.remainingPayments > 1 ? "s" : ""})`;
    },
  },
];

export default function SailorAgreements() {
  const sailor = useSailorContext();
  const { sailorAgreements, loading, error } = useSailorAgreements(sailor.id);
  const { setError } = useErrorMessage();

  if (error) {
    setError(error);
  }

  if (loading || !sailorAgreements) {
    return (
      <CircularProgress sx={{ alignSelf: "center", justifySelf: "center" }} />
    );
  }

  return (
    <DataGridContainer>
      <DataGrid
        columns={columns}
        rows={sailorAgreements}
        doubleClickSlug="agreements"
        initialState={{
          sorting: {
            sortModel: [{ field: "agreementCreation", sort: "desc" }],
          },
        }}
      />
    </DataGridContainer>
  );
}
